'use client'

import { yupResolver } from '@hookform/resolvers/yup'
import { LineVideoCameraPlus } from '@qr-front/icons/line-video-camera-plus'
import { Button } from '@qr-front/ui/components/Button'
import {
	FileUpload,
	TFileUploadHandle,
} from '@qr-front/ui/components/FileUpload'
import { isFileUploadingAtom } from 'components/FileUploadToMediaStorage'
import { showNotification } from 'components/Notification'
import { GENERATOR_FORM_ANIMATION_DURATION } from 'features/generator/components/GeneratorFormSection'
import { RhcField } from 'features/generator/components/RhcField'
import { isSubmittingAtom } from 'features/generator/GeneratorLayout/GeneratorFooter/NextButton'
import { useSyncFieldWithAtom } from 'hooks/useSyncFieldWithAtom'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { atomWithReset, RESET } from 'jotai/utils'
import { useTranslations } from 'next-intl'
import {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from 'react'
import { FormProvider, Resolver, useForm } from 'react-hook-form'
import { sanitizeVideoUrl } from 'utils/sanitizeVideoUrl'
import * as yup from 'yup'
import { InferType } from 'yup'

import { getStore } from '@/store'
import { TTranslationFunction } from '@/types'
import { validateContentForm } from '@/utils'

import { urlSchema } from '../fieldSchemas'
import {
	TContentFormHandle,
	TQrContentFormSection,
} from '../qrContentFormTypes'
import { useSyncFormSectionValidity } from '../useSyncFormSectionValidity'
import { uploadingVideoFilesAtom, useVideo } from './useVideo'
import { TVideo } from './VideoFormModel'
import {
	VideoDetailsFields,
	videoListAtom,
} from './VideoSection/VideoDetailsFields'

export const VIDEO_FORM_ID = 'video'
export const VIDEO_URL_MAX_LENGTH = 100
// 100MB
export const MAX_FILE_SIZE = VIDEO_URL_MAX_LENGTH * 1024 * 1024
export const MAX_VIDEO_COUNT = 10
export const videoUrlAtom = atomWithReset('')

export const ACCEPTED_MIME_TYPES = [
	'video/mp4',
	'video/ogg',
	'video/webm',
	'video/x-msvideo',
	'video/quicktime',
	'audio/ogg',
	'video/x-ms-wmv',
]

export enum TransformationStatusEnum {
	NOT_YET_APPLIED = 'not_yet_applied',
	TRANSFORMED = 'transformed',
	TRANSFORMED_FFMPEG = 'transformed_ffmpeg',
	NOT_APPLICABLE = 'not_applicable',
	FAILED = 'failed',
}

export type CloudinaryMediaDetailsDTO = {
	publicId: string
	key?: string
	resourceType: string
	format: string
	bytes: number
	storageProvider?: string
	hasAutoTransformation?: TransformationStatusEnum
	bucketRootUrl?: string
}

export const getVideoSchema = (t: TTranslationFunction) =>
	yup.object({
		url: urlSchema(t, { required: false, maxLength: VIDEO_URL_MAX_LENGTH }),
	})

export type TVideoFormField = InferType<ReturnType<typeof getVideoSchema>>

export const resetVideoForm = () => {
	getStore().set(videoUrlAtom, RESET)
}

export const resetVideoList = () => {
	getStore().set(videoListAtom, RESET)
	getStore().set(uploadingVideoFilesAtom, [])
}

const hasSource = (video: TVideo) =>
	!!(video.video.source?.uploaded?.publicId || video.video.source?.url)

const getIsVideoFormValid = ({
	videoList,
	progress,
}: {
	videoList: TVideo[]
	progress: Record<string, number>
}) =>
	videoList.length >= 1 &&
	videoList.every(hasSource) &&
	Object.values(progress).every((item) => item === 100)

export const VideoForm = forwardRef<TContentFormHandle>((_, ref) => {
	const t = useTranslations()
	const isSubmitting = useAtomValue(isSubmittingAtom)
	const setIsFileUploading = useSetAtom(isFileUploadingAtom)

	const formRef = useRef<HTMLFormElement>(null)
	const titleFieldRefs = useRef<TContentFormHandle>(null)
	const uploadSemaphoreRef = useRef<number>(0)

	const [videoUrl, setVideoUrl] = useAtom(videoUrlAtom)

	const {
		progress,
		upload,
		add,
		videoList,
		fileDelete,
		files,
		removeProgress,
	} = useVideo()

	const [canPlayFn, setCanPlayFn] = useState<
		((url: string) => boolean) | null
	>(null)

	const isVideoLimitReached = videoList.length >= 10

	useEffect(() => {
		Promise.all([
			import('react-player/youtube'),
			import('react-player/vimeo'),
		]).then(([YouTube, Vimeo]) => {
			setCanPlayFn(() => (url: string) => {
				return (
					YouTube.default.canPlay(url) || Vimeo.default.canPlay(url)
				)
			})
		})
	}, [])

	const form = useForm<TVideoFormField>({
		defaultValues: {
			url: getStore().get(videoUrlAtom),
		},
		mode: 'onTouched',
		reValidateMode: 'onChange',
		resolver: yupResolver(
			getVideoSchema(t),
		) as unknown as Resolver<TVideoFormField>,
		shouldUnregister: false,
	})

	const {
		formState: { errors },
		setValue,
		setError,
		handleSubmit,
	} = form

	useSyncFieldWithAtom(
		{
			atom: videoUrlAtom,
			setFieldValue: (value) => {
				setValue('url', value)
			},
		},
		[videoUrlAtom],
	)

	const addLocal = useCallback(() => {
		if (!canPlayFn || !videoUrl) {
			return
		}

		if (!canPlayFn(videoUrl)) {
			setError('url', {
				message: t(
					'generator__content_form_section__videos__input__error__link',
				),
			})
			return
		}
		add()
		resetVideoForm()
	}, [add, canPlayFn, setError, t, videoUrl])

	const showMaxFilesError = useCallback(
		(count: number) => {
			showNotification(
				t('generator__content_form_section__videos__maxfiles', {
					count,
				}),
				{ type: 'error' },
			)
		},
		[t],
	)

	const handleFileUpload = useCallback(
		async (file: File) => {
			// Semaphore-like check: consider both current videos and ongoing uploads
			const totalVideos = videoList.length + uploadSemaphoreRef.current
			if (totalVideos >= MAX_VIDEO_COUNT) {
				showMaxFilesError(MAX_VIDEO_COUNT)
				return
			}
			// Increment semaphore
			uploadSemaphoreRef.current += 1
			setIsFileUploading(true)
			try {
				const allUploaded = await upload(file)
				if (allUploaded) {
					setIsFileUploading(false)
				}
			} finally {
				// Decrement semaphore when upload completes (success or failure)
				uploadSemaphoreRef.current = Math.max(
					0,
					uploadSemaphoreRef.current - 1,
				)
				console.log('[handleFileUpload] Upload completed', {
					currentVideos: videoList.length,
					ongoingUploads: uploadSemaphoreRef.current,
				})
			}
		},
		// TODO: Fix exhaustive deps warning
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[videoList.length, setIsFileUploading, upload],
	)

	const handleVideoRemove = useCallback(
		(video: TVideo) => {
			removeProgress(video.video.originalName || '')
		},
		[removeProgress],
	)

	const handleFileDelete = useCallback(
		(file: File, indexToReplace: number) => {
			fileDelete(file, indexToReplace)
		},
		[fileDelete],
	)

	const fileUploadRef = useRef<TFileUploadHandle>(null)

	const isVideoFormValid = useMemo(
		() => getIsVideoFormValid({ videoList, progress }),
		[videoList, progress],
	)

	useSyncFormSectionValidity({
		formId: VIDEO_FORM_ID,
		isValid: isVideoFormValid,
	})

	useEffect(() => {
		if (isVideoFormValid) {
			fileUploadRef.current?.clearError?.()
		}
	}, [isVideoFormValid])

	useImperativeHandle(
		ref,
		() => ({
			validate: async () => {
				const formValidationResult = await new Promise<{
					isValid: boolean
				}>((resolve) => {
					validateContentForm(
						(onValid, onInvalid) =>
							handleSubmit(onValid, async (errors) => {
								onInvalid(errors)
							}),
						0,
					).then(resolve)
				})

				if (!formValidationResult.isValid) {
					return { isValid: false }
				}

				const areTitlesValid = await titleFieldRefs?.current?.validate()
				if (!areTitlesValid?.isValid) {
					return { isValid: false }
				}
				const videoList = getStore().get(videoListAtom)
				const currentValid = getIsVideoFormValid({
					videoList,
					progress,
				})
				if (currentValid) {
					fileUploadRef.current?.clearError?.()
					return { isValid: true }
				}
				fileUploadRef.current?.setError?.(
					t(
						'generator__content_form_section__videos__no_video_error',
					),
				)
				setTimeout(() => {
					formRef.current?.scrollIntoView?.({
						behavior: 'smooth',
						block: 'center',
					})
				}, GENERATOR_FORM_ANIMATION_DURATION * 1.25)
				showNotification(
					t(
						'generator__content_form_section__videos__no_video_error',
					),
					{ type: 'error' },
				)
				return { isValid: false }
			},
		}),
		[progress, t, handleSubmit],
	)

	return (
		<FormProvider {...form}>
			<form
				noValidate
				className="flex flex-col gap-2 tablet:gap-4"
				ref={formRef}
				onSubmit={handleSubmit(() => addLocal())}
			>
				<div className="flex items-start space-x-4">
					<div className="flex-1 pb-[1rem]">
						<RhcField
							type="url"
							form={form}
							path="url"
							value={videoUrl}
							setValue={(value) => setVideoUrl(value || '')}
							fieldName="video_url"
							onBlur={() => {
								if (videoUrl) {
									setVideoUrl(sanitizeVideoUrl(videoUrl))
								}
							}}
							label={t(
								'generator__content_form_section__videos__input__label',
							)}
							placeholder={t(
								'generator__content_form_section__videos__input__placeholder',
							)}
							className="w-full"
							disabled={isVideoLimitReached}
							error={errors.url?.message}
						/>
					</div>
					<Button
						type="submit"
						className="mt-[2rem] w-fit [&>svg]:size-4"
						size="large"
						disabled={
							isVideoLimitReached ||
							!videoUrl ||
							!!errors.url?.message
						}
					>
						{t('generator__content_form_section__videos__button')}
					</Button>
				</div>
				<FileUpload
					files={files}
					ref={fileUploadRef}
					onFileUpload={handleFileUpload}
					onFileReplace={() => {}}
					onFileDelete={handleFileDelete}
					progress={progress}
					maxFiles={MAX_VIDEO_COUNT}
					maxFileSize={MAX_FILE_SIZE}
					accept={ACCEPTED_MIME_TYPES}
					onMaxFilesExceeded={showMaxFilesError}
					placeholderIcon={<LineVideoCameraPlus />}
					translations={{
						title: t(
							'generator__content_form_section__videos__headline__label',
						),
						caption: t(
							'generator__content_form_section__videos__headline__caption',
							{ size: 100 },
						),
						deleteButtonLabel: '',
						unsupportedFormatErrorMessage: (errors: string[]) => {
							if (errors.includes('FILE_INVALID_TYPE')) {
								showNotification(
									t(
										'generator__content_form_section__videos__wrong__format',
									),
									{ type: 'error' },
								)
							}
						},
						maxFilesLimitError: () => {
							showMaxFilesError(MAX_VIDEO_COUNT)
						},
						sizeTooLargeErrorMessage: t(
							'ui__file_upload__file_size_error',
							{
								maxSize: MAX_FILE_SIZE / 1024 / 1024,
							},
						),
						itemPreview: (file: File) => {
							return file.name
						},
						deleteFile: (file: File) => {
							return file.name
						},
					}}
					disabled={isSubmitting}
					hideDropZone={isVideoLimitReached}
					hideMultiFilePreview
				/>
				<VideoDetailsFields
					ref={titleFieldRefs}
					onVideoRemove={handleVideoRemove}
				/>
			</form>
		</FormProvider>
	)
})

VideoForm.displayName = 'VideoForm'

export const videoFormConfig = (
	t: TTranslationFunction,
): TQrContentFormSection => ({
	id: VIDEO_FORM_ID,
	title: t('generator__content_form_section__videos__title'),
	description: t('generator__content_form_section__videos__subtitle'),
	icon: <LineVideoCameraPlus />,
	Content: VideoForm,
})
